// src/pages/StockAllocation.js
import React, { useEffect, useMemo, useState } from "react";
import { API_BASE } from "../config";
import { authHeaders } from "../utils/authHeaders";

const joinApiUrl = (path) => {
  if (!path) return "";
  const base = API_BASE.replace(/\/$/, ""); // remove trailing /
  const cleaned = path.startsWith("/api/") ? path.slice(4) : path; // drop leading "/api"
  return `${base}${cleaned.startsWith("/") ? "" : "/"}${cleaned}`;
};

// ✅ PART 1 — Professional Table Layout (Alignment Fix)
const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  tableLayout: "fixed",
};

const thStyle = {
  padding: "10px",
  textAlign: "left",
  borderBottom: "1px solid #ccc",
  fontWeight: 600,
};

const tdStyle = {
  padding: "10px",
  borderBottom: "1px solid #eee",
  verticalAlign: "top",
};

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : NaN;
};

export default function StockAllocation() {
  const [agents, setAgents] = useState([]);
  const [products, setProducts] = useState([]); // product list for dropdown
  const [allocations, setAllocations] = useState([]); // summary table
  const [history, setHistory] = useState([]); // session history table

  // availability map: product_id -> available_qty (warehouse FEFO)
  const [availMap, setAvailMap] = useState(new Map());

  // Agent selection
  const [selectedAgentId, setSelectedAgentId] = useState("");
  const [activeAgent, setActiveAgent] = useState(null);

  // Allocation lines
  const [rows, setRows] = useState([{ product_id: "", qty: "", agent_issue_rate: "" }]);
  const [remarks, setRemarks] = useState("");

  // UI states
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
const [, setLastResult] = useState(null);

  // 🔵 STEP 1 — Add Tab + Filter State
  const [activeTab, setActiveTab] = useState("ALLOCATE"); // ALLOCATE | HISTORY | SUMMARY

  const [agentFilter, setAgentFilter] = useState([]);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [rowsPerPageHistory, setRowsPerPageHistory] = useState(50);
  const [rowsPerPageSummary, setRowsPerPageSummary] = useState(50);

  // ✅ PART 2 — Proper Pagination State
  const [historyPage, setHistoryPage] = useState(1);
  const [summaryPage, setSummaryPage] = useState(1);

  useEffect(() => {
    setHistoryPage(1);
  }, [agentFilter, fromDate, toDate, rowsPerPageHistory]);

  useEffect(() => {
    setSummaryPage(1);
  }, [agentFilter, fromDate, toDate, rowsPerPageSummary]);

  // ------------------------------
  // LOAD AGENTS + PRODUCTS
  // ------------------------------
  const loadAgents = async () => {
    const res = await fetch(`${API_BASE}/agents`, {
      headers: authHeaders(),
    });
    const data = await res.json();
    setAgents(Array.isArray(data) ? data : []);
  };

  const fetchProducts = async () => {
    const res = await fetch(`${API_BASE}/products/list`, {
      headers: authHeaders(),
    });
    const data = await res.json();
    setProducts(Array.isArray(data) ? data : []);
  };

  // ------------------------------
  // LOAD STOCK ALLOCATION SUMMARY (ledger-based)
  // ------------------------------
  const loadAllocations = async () => {
    try {
      const res = await fetch(`${API_BASE}/stock/allocation/list`, {
        headers: authHeaders(),
      });
      const data = await res.json();
      setAllocations(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error loading allocation list:", err);
    }
  };

  // ------------------------------
  // LOAD SESSION HISTORY
  // ------------------------------
  const loadHistory = async () => {
    try {
      const res = await fetch(`${API_BASE}/stock/allocations/history`, {
        headers: authHeaders(),
      });
      const data = await res.json();
      if (data?.success) setHistory(data.data || []);
      else setHistory([]);
    } catch (err) {
      console.error("Error loading allocation history:", err);
    }
  };

  // ------------------------------
  // LOAD FEFO WAREHOUSE AVAILABILITY (recommended)
  // Endpoint should return array: [{product_id, available_qty}]
  // ------------------------------
  const loadWarehouseAvailability = async () => {
    const res = await fetch(`${API_BASE}/stock/available?scope=warehouse`, {
      headers: authHeaders(),
    });
    const data = await res.json();

    if (data?.success && Array.isArray(data.data)) {
      const m = new Map();
      data.data.forEach((r) => m.set(String(r.product_id), Number(r.available_qty || 0)));
      setAvailMap(m);
    } else {
      setAvailMap(new Map());
    }
  };

  useEffect(() => {
    loadAgents();
    fetchProducts();
    loadAllocations();
    loadHistory();
    loadWarehouseAvailability();
  }, []);

  // ------------------------------
  // Start allocation session for agent
  // ------------------------------
  const startAllocation = () => {
    if (!selectedAgentId) {
      alert("Select agent first");
      return;
    }
    const a = agents.find((x) => String(x.id) === String(selectedAgentId));
    setActiveAgent(a || { id: selectedAgentId, name: `Agent #${selectedAgentId}` });
    setLastResult(null);
  };

  // ------------------------------
  // Grid helpers
  // ------------------------------
  const addRow = () => setRows((prev) => [...prev, { product_id: "", qty: "", agent_issue_rate: "" }]);
  const removeRow = (idx) => setRows((prev) => prev.filter((_, i) => i !== idx));
  const patchRow = (idx, patch) =>
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const lineTotal = (r) => {
    const q = num(r.qty);
    const rate = num(r.agent_issue_rate);
    if (!Number.isFinite(q) || !Number.isFinite(rate)) return 0;
    return Math.round(q * rate * 100) / 100;
  };

  const grandTotal = useMemo(() => rows.reduce((s, r) => s + lineTotal(r), 0), [rows]);

  const validate = () => {
    if (!activeAgent?.id) return "Please select agent and click Start Allocation.";

    for (let i = 0; i < rows.length; i++) {
      const r = rows[i];
      if (!r.product_id) return `Row ${i + 1}: Select product`;

      const q = num(r.qty);
      if (!Number.isFinite(q) || q <= 0) return `Row ${i + 1}: Enter valid qty`;

      const rate = num(r.agent_issue_rate);
      if (!Number.isFinite(rate) || rate <= 0) return `Row ${i + 1}: Enter valid agent issue rate`;

      // Optional frontend safety if availability map exists
      const available = availMap.get(String(r.product_id));
      if (available != null && q > available) return `Row ${i + 1}: Qty exceeds available (${available})`;
    }
    return null;
  };

  // ------------------------------
  // Submit session
  // ------------------------------
  const submit = async () => {
    const err = validate();
    if (err) return alert(err);
    setConfirmOpen(true);
  };

  const confirmSubmit = async () => {
    try {
      setConfirmOpen(false);
      setLoading(true);

      const payload = {
        agent_id: Number(activeAgent.id),
        remarks: remarks || null,
        items: rows.map((r) => ({
          product_id: Number(r.product_id),
          qty: Number(r.qty),
          agent_issue_rate: Number(r.agent_issue_rate),
        })),
      };

      const res = await fetch(`${API_BASE}/stock/allocate-session-pending`, {
        method: "POST",
        headers: authHeaders({
          "Content-Type": "application/json",
        }),
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!data?.success) {
        alert(data?.message || "Allocation failed");
        return;
      }

      setLastResult(data.data);

      // reset grid
      setRows([{ product_id: "", qty: "", agent_issue_rate: "" }]);
      setRemarks("");

      // refresh tables
      await loadAllocations();
      await loadHistory();
      await loadWarehouseAvailability();

      alert(
  `Allocation ${data.data?.allocation_no || ""} has been sent to agent for confirmation.`
);
    } catch (err) {
      console.error(err);
      alert("Server error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: "20px" }}>
      <h2>Stock Allocation</h2>

      {/* 🔵 STEP 2 — Add Tab Buttons (Above everything) */}
      <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
        {[
          { key: "ALLOCATE", label: "Stock Allocation" },
          { key: "HISTORY", label: "Allocation History" },
          { key: "SUMMARY", label: "Stock Allocation Summary" },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: "10px 16px",
              borderRadius: 8,
              border: "1px solid #ccc",
              background: activeTab === tab.key ? "#0066ff" : "#fff",
              color: activeTab === tab.key ? "#fff" : "#000",
              cursor: "pointer",
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* 🔵 STEP 3 — Wrap Sections by Tab */}
      {/* 🟢 Wrap Allocation Section */}
      {activeTab === "ALLOCATE" && (
        <>
          {/* ================= TOP BAR ================= */}
          <div
            style={{
              background: "#fff",
              padding: 16,
              borderRadius: 10,
              boxShadow: "0 0 10px rgba(0,0,0,0.08)",
              marginBottom: 20,
              display: "flex",
              gap: 20,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <div style={{ minWidth: 280 }}>
              <div style={{ fontSize: 12, marginBottom: 4 }}>Select Agent</div>
              <select
                value={selectedAgentId}
                onChange={(e) => setSelectedAgentId(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px",
                  border: "1px solid #ccc",
                  borderRadius: 6,
                }}
              >
                <option value="">Select Agent</option>
                {agents.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name} ({a.phone || "-"})
                  </option>
                ))}
              </select>
            </div>

            <button
              onClick={startAllocation}
              style={{
                padding: "10px 20px",
                background: "#0066ff",
                color: "#fff",
                border: "none",
                borderRadius: 6,
                cursor: "pointer",
              }}
            >
              Start Allocation
            </button>

            {activeAgent && (
              <div style={{ marginLeft: "auto", fontWeight: 600 }}>
                Agent: {activeAgent.name} | {new Date().toLocaleString("en-IN")}
              </div>
            )}
          </div>

          {/* ================= GRID ================= */}
          <div
            style={{
              background: "#fff",
              padding: 16,
              borderRadius: 10,
              boxShadow: "0 0 10px rgba(0,0,0,0.08)",
            }}
          >
            <h3 style={{ marginBottom: 10 }}>Allocate Stock (Multi-line)</h3>

            {!activeAgent ? (
              <div style={{ opacity: 0.6 }}>Select agent and click “Start Allocation”.</div>
            ) : (
              <>
                <table style={tableStyle}>
                  <colgroup>
                    <col style={{ width: "60px" }} />
                    <col style={{ width: "30%" }} />
                    <col style={{ width: "100px" }} />
                    <col style={{ width: "120px" }} />
                    <col style={{ width: "140px" }} />
                    <col style={{ width: "140px" }} />
                    <col style={{ width: "60px" }} />
                  </colgroup>

                  <thead>
                    <tr style={{ background: "#f0f0f0" }}>
                      <th style={thStyle}>Sl</th>
                      <th style={thStyle}>Stock / Product</th>
                      <th style={{ ...thStyle, textAlign: "right" }}>Available</th>
                      <th style={thStyle}>Allocated</th>
                      <th style={thStyle}>Agent Issue Rate</th>
                      <th style={{ ...thStyle, textAlign: "right" }}>Total</th>
                      <th style={thStyle}></th>
                    </tr>
                  </thead>

                  <tbody>
                    {rows.map((r, idx) => {
                      const available = r.product_id ? availMap.get(String(r.product_id)) ?? 0 : null;

                      return (
                        <tr key={idx}>
                          <td style={tdStyle}>{idx + 1}</td>

                          <td style={tdStyle}>
                            <select
                              value={r.product_id}
                              onChange={(e) => patchRow(idx, { product_id: e.target.value })}
                              style={{
                                width: "100%",
                                padding: "8px",
                                border: "1px solid #ccc",
                                borderRadius: 6,
                              }}
                            >
                              <option value="">Select Product</option>
                              {products.map((p) => (
                                <option key={p.id} value={p.id}>
                                  {p.name}
                                </option>
                              ))}
                            </select>
                          </td>

                          <td style={{ ...tdStyle, textAlign: "right" }}>{available == null ? "-" : available}</td>

                          <td style={tdStyle}>
                            <input
                              type="number"
                              value={r.qty}
                              onChange={(e) => patchRow(idx, { qty: e.target.value })}
                              style={{
                                width: "100%",
                                padding: "8px",
                                border: "1px solid #ccc",
                                borderRadius: 6,
                              }}
                            />
                          </td>

                          <td style={tdStyle}>
                            <input
                              type="number"
                              step="0.01"
                              value={r.agent_issue_rate}
                              onChange={(e) => patchRow(idx, { agent_issue_rate: e.target.value })}
                              style={{
                                width: "100%",
                                padding: "8px",
                                border: "1px solid #ccc",
                                borderRadius: 6,
                              }}
                            />
                          </td>

                          <td style={{ ...tdStyle, textAlign: "right", fontWeight: 600 }}>
                            ₹ {lineTotal(r).toFixed(2)}
                          </td>

                          <td style={tdStyle}>
                            {rows.length > 1 && (
                              <button
                                onClick={() => removeRow(idx)}
                                style={{
                                  border: "none",
                                  background: "transparent",
                                  cursor: "pointer",
                                  fontSize: 16,
                                }}
                              >
                                ✖
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>

                  <tfoot>
                    <tr>
                      <td colSpan={5}></td>
                      <td style={{ ...tdStyle, textAlign: "right", fontWeight: 700 }}>
                        Grand Total: ₹ {grandTotal.toFixed(2)}
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>

                <div style={{ marginTop: 12 }}>
                  <button onClick={addRow} style={{ padding: 8 }}>
                    + Add Row
                  </button>
                </div>

                <textarea
                  placeholder="Remarks (optional)"
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  style={{
                    width: "100%",
                    marginTop: 12,
                    padding: 10,
                    border: "1px solid #ccc",
                    borderRadius: 6,
                  }}
                />

                <button
                  onClick={submit}
                  disabled={loading}
                  style={{
                    marginTop: 12,
                    width: "100%",
                    padding: 12,
                    background: "#0066ff",
                    color: "#fff",
                    border: "none",
                    borderRadius: 6,
                    cursor: "pointer",
                  }}
                >
                {loading ? "Submitting..." : "Submit for Agent Confirmation"}
                </button>
              </>
            )}
          </div>

          {/* ----------------- CONFIRM MODAL ----------------- */}
          {confirmOpen && (
            <div
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,0.4)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 9999,
              }}
            >
              <div style={{ background: "#fff", padding: 16, borderRadius: 10, width: 420 }}>
                <h3>Confirm Allocation</h3>
                <p>
                  Are you sure you want to send these items to <b>{activeAgent?.name}</b> for confirmation?
                </p>

                <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
                  <button onClick={() => setConfirmOpen(false)} style={{ padding: "10px 12px" }}>
                    No
                  </button>
                  <button
                    onClick={confirmSubmit}
                    style={{ padding: "10px 12px", background: "#0066ff", color: "#fff", border: "none" }}
                  >
                  Yes, Send for Confirmation
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* 🟢 Wrap History Section */}
      {activeTab === "HISTORY" && (
        <>
          <FilterBar
            agents={agents}
            agentFilter={agentFilter}
            setAgentFilter={setAgentFilter}
            fromDate={fromDate}
            setFromDate={setFromDate}
            toDate={toDate}
            setToDate={setToDate}
            rows={rowsPerPageHistory}
            setRows={setRowsPerPageHistory}
          />

          <HistoryTable
            history={history}
            agents={agents}
            agentFilter={agentFilter}
            fromDate={fromDate}
            toDate={toDate}
            rowsPerPage={rowsPerPageHistory}
            joinApiUrl={joinApiUrl}
            page={historyPage}
            setPage={setHistoryPage}
          />
        </>
      )}

      {/* 🟢 Wrap Summary Section */}
      {activeTab === "SUMMARY" && (
        <>
          <FilterBar
            agents={agents}
            agentFilter={agentFilter}
            setAgentFilter={setAgentFilter}
            fromDate={fromDate}
            setFromDate={setFromDate}
            toDate={toDate}
            setToDate={setToDate}
            rows={rowsPerPageSummary}
            setRows={setRowsPerPageSummary}
          />

          <SummaryTable
            allocations={allocations}
            agents={agents}
            agentFilter={agentFilter}
            fromDate={fromDate}
            toDate={toDate}
            rowsPerPage={rowsPerPageSummary}
            page={summaryPage}
            setPage={setSummaryPage}
          />
        </>
      )}
    </div>
  );
}

// 🔵 STEP 4 — Add These 3 Small Components Below Your Main Component

// 🟣 FilterBar Component
function FilterBar({
  agents,
  agentFilter,
  setAgentFilter,
  fromDate,
  setFromDate,
  toDate,
  setToDate,
  rows,
  setRows,
}) {
  return (
    <div
      style={{
        display: "flex",
        gap: 20,
        flexWrap: "wrap",
        background: "#fff",
        padding: 16,
        borderRadius: 8,
        boxShadow: "0 0 10px rgba(0,0,0,0.06)",
        marginBottom: 16,
      }}
    >
      <div>
        <div style={{ fontSize: 12 }}>Agent (Ctrl + Click)</div>
        <select
          multiple
          value={agentFilter}
          onChange={(e) => setAgentFilter(Array.from(e.target.selectedOptions).map((o) => o.value))}
          style={{ minWidth: 200, height: 90 }}
        >
          {agents.map((a) => (
            <option key={a.id} value={String(a.id)}>
              {a.name}
            </option>
          ))}
        </select>
        <div>
          <button onClick={() => setAgentFilter([])}>All</button>
        </div>
      </div>

      <div>
        <div style={{ fontSize: 12 }}>From</div>
        <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
      </div>

      <div>
        <div style={{ fontSize: 12 }}>To</div>
        <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
      </div>

      <div>
        <div style={{ fontSize: 12 }}>Rows</div>
        <select value={rows} onChange={(e) => setRows(Number(e.target.value))}>
          {[50, 100, 200, 500].map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

// 🟣 HistoryTable Component
function HistoryTable({ history, agents, agentFilter, fromDate, toDate, rowsPerPage, joinApiUrl, page, setPage }) {
  const agentNames = new Set(
    agentFilter.length ? agents.filter((a) => agentFilter.includes(String(a.id))).map((a) => a.name) : []
  );

  const filtered = history.filter((r) => {
    if (agentFilter.length && !agentNames.has(r.agent_name)) return false;

    const d = new Date(r.allocation_date);
    if (fromDate && d < new Date(fromDate + "T00:00:00")) return false;
    if (toDate && d > new Date(toDate + "T23:59:59")) return false;

    return true;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / rowsPerPage));
  const start = (page - 1) * rowsPerPage;
  const pagedRows = filtered.slice(start, start + rowsPerPage);

  return (
    <>
      <table style={tableStyle}>
        <thead>
          <tr style={{ background: "#ddd" }}>
  <th style={thStyle}>Date</th>
  <th style={thStyle}>Agent</th>
  <th style={thStyle}>Allocation No</th>
  <th style={thStyle}>Status</th>
  <th style={thStyle}>Confirmation</th>
  <th style={thStyle}>Lines</th>
  <th style={thStyle}>Total</th>
  <th style={thStyle}>PDF</th>
</tr>
        </thead>
        <tbody>
          {pagedRows.map((r) => (
           <tr key={r.id}>
  <td style={tdStyle}>{new Date(r.allocation_date).toLocaleString()}</td>
  <td style={tdStyle}>{r.agent_name}</td>
  <td style={tdStyle}>{r.allocation_no}</td>

  <td style={tdStyle}>
    <span
      style={{
        padding: "4px 8px",
        borderRadius: 12,
        fontSize: 12,
        fontWeight: 700,
        background:
          r.status === "ACCEPTED"
            ? "#e6f4ea"
            : r.status === "REJECTED"
            ? "#fdecea"
            : "#fff4e5",
        color:
          r.status === "ACCEPTED"
            ? "#137333"
            : r.status === "REJECTED"
            ? "#a50e0e"
            : "#b06000",
      }}
    >
      {r.status || "ACCEPTED"}
    </span>
  </td>

  <td style={tdStyle}>
    {r.status === "PENDING" && "Waiting for agent"}
    {r.status === "ACCEPTED" &&
      (r.accepted_at
        ? `Accepted on ${new Date(r.accepted_at).toLocaleString()}`
        : "Accepted")}
    {r.status === "REJECTED" &&
      `Rejected${r.rejected_at ? ` on ${new Date(r.rejected_at).toLocaleString()}` : ""}${
        r.agent_action_remarks ? ` — ${r.agent_action_remarks}` : ""
      }`}
  </td>

  <td style={tdStyle}>{r.total_lines}</td>
  <td style={tdStyle}>₹ {Number(r.total_cost).toFixed(2)}</td>

  <td style={tdStyle}>
    {r.pdf_url ? (
      <a href={joinApiUrl(r.pdf_url)} target="_blank" rel="noreferrer">
        Download
      </a>
    ) : (
      <span style={{ opacity: 0.6 }}>Available after acceptance</span>
    )}
  </td>
</tr>
          ))}
        </tbody>
      </table>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 10 }}>
        <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
          Prev
        </button>
        <div>
          Page {page} / {totalPages}
        </div>
        <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
          Next
        </button>
      </div>
    </>
  );
}

// 🟣 SummaryTable Component
function SummaryTable({ allocations, agents, agentFilter, fromDate, toDate, rowsPerPage, page, setPage }) {
  const agentNames = new Set(
    agentFilter.length ? agents.filter((a) => agentFilter.includes(String(a.id))).map((a) => a.name) : []
  );

  const filtered = allocations.filter((r) => {
    if (agentFilter.length && !agentNames.has(r.agent_name)) return false;

    const d = new Date(r.updated_at);
    if (fromDate && d < new Date(fromDate + "T00:00:00")) return false;
    if (toDate && d > new Date(toDate + "T23:59:59")) return false;

    return true;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / rowsPerPage));
  const start = (page - 1) * rowsPerPage;
  const pagedRows = filtered.slice(start, start + rowsPerPage);

  return (
    <>
      <table style={tableStyle}>
        <thead>
          <tr style={{ background: "#ddd" }}>
            <th style={thStyle}>Agent</th>
            <th style={thStyle}>Product</th>
            <th style={thStyle}>Opening</th>
            <th style={thStyle}>Received</th>
            <th style={thStyle}>Closing</th>
            <th style={thStyle}>Updated</th>
          </tr>
        </thead>
        <tbody>
          {pagedRows.map((row, index) => (
            <tr key={index}>
              <td style={tdStyle}>{row.agent_name}</td>
              <td style={tdStyle}>{row.product_name}</td>
              <td style={tdStyle}>{row.opening}</td>
              <td style={tdStyle}>{row.received}</td>
              <td style={tdStyle}>{row.closing}</td>
              <td style={tdStyle}>{new Date(row.updated_at).toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 10 }}>
        <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
          Prev
        </button>
        <div>
          Page {page} / {totalPages}
        </div>
        <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
          Next
        </button>
      </div>
    </>
  );
}
